import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { BoardSectionStoryblok } from '../../types/storyblok';
import { BoardMember } from './BoardMember';

// Decorative assets from Figma
import ornament4Src from '../../images/board/ornament-4.svg';
import ornament5Src from '../../images/board/ornament-5.svg';
import blueShadeSrc from '../../images/board/blue-shade.svg';

interface BoardSectionProps {
  blok: BoardSectionStoryblok;
}

/**
 * BoardSection displays board members in a centered grid layout.
 *
 * Desktop: 4 columns with centered rows
 * Mobile: 1-2 columns responsive
 *
 * Includes decorative elements: blue shade gradient, ornaments
 */
export const BoardSection: React.FC<BoardSectionProps> = ({ blok }) => {
  const eyebrow = blok.eyebrow || 'OUR BOARD';
  const members = blok.members ?? [];
  const headlineId = React.useId();

  return (
    <section
      {...storyblokEditable(blok as unknown as SbBlokData)}
      aria-labelledby={headlineId}
      className="relative bg-neutral-off-white py-[158px] px-4 desktop:px-grid-margin-desktop"
    >
      {/* Decorative blue shade - bottom right (Figma: left 1167px, top 637px, 462x462) */}
      <img
        src={blueShadeSrc}
        alt=""
        aria-hidden="true"
        className="absolute hidden desktop:block pointer-events-none select-none"
        style={{
          width: '862px',
          height: '862px',
          right: '-127px',
          bottom: '-200px',
        }}
      />

      {/* Ornament 4 - top right, overflows above section
          Figma: left 1186px, top -69px, container 359x359, rotate(-142.33deg) scaleY(-1) */}
      <img
        src={ornament4Src}
        alt=""
        aria-hidden="true"
        className="absolute hidden desktop:block pointer-events-none select-none"
        style={{
          width: '359px',
          height: '359px',
          right: '-105px',
          top: '-69px',
          transform: 'scaleY(-1) rotate(-142.33deg)',
        }}
      />

      {/* Ornament 5 - left side, overflows left of section
          Figma: left -173px, top 403.69px, container 362x362, rotate(-46.74deg) scaleY(-1) */}
      <img
        src={ornament5Src}
        alt=""
        aria-hidden="true"
        className="absolute hidden desktop:block pointer-events-none select-none"
        style={{
          width: '362px',
          height: '362px',
          left: '-173px',
          top: '403.69px',
          transform: 'scaleY(-1) rotate(-46.74deg)',
        }}
      />

      {/* Main content */}
      <div className="relative z-10 max-w-[1061px] mx-auto flex flex-col items-center gap-[94px]">
        {/* Header */}
        <div className="flex flex-col items-center gap-2 text-center w-full">
          <span className="text-sm font-extrabold uppercase tracking-[0.16em] text-brand-mid">
            {eyebrow}
          </span>
          <h2 id={headlineId} className="text-[44px] font-extrabold text-brand-deep leading-none">
            {blok.headline}
          </h2>
        </div>

        {/* Board members grid - max 4 per row */}
        {members.length > 0 && (
          <div className="grid grid-cols-2 desktop:grid-cols-4 gap-8 justify-items-center">
            {members.map((member) => (
              <BoardMember key={member._uid} blok={member} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
